/**
 * Glyphs for the Modal & Dialog System header tile, traced from
 * `Modal System.dc.html`.
 *
 * All four are stroke-only and paint with `currentColor`, so the tile sets the
 * tone colour once and the glyph follows. They are decorative — the accessible
 * name comes from the dialog title — hence `aria-hidden`.
 */

interface GlyphProps {
  /** Edge length in px. Defaults to the header tile's 19px. */
  size?: number;
}

const base = (size: number) => ({
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.9,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  width: size,
  height: size,
  "aria-hidden": true,
  focusable: false,
});

/** Default tone — documents, sending, generic actions. */
export const ModalDocIcon = ({ size = 19 }: GlyphProps) => (
  <svg {...base(size)}>
    <path d="M7 3h7l4 4v14H7z" />
    <path d="M14 3v4h4" />
  </svg>
);

/** Destructive tone — delete, void, revoke. */
export const ModalTrashIcon = ({ size = 19 }: GlyphProps) => (
  <svg {...base(size)}>
    <path d="M4 7h16M9 7V5h6v2" />
    <path d="M7 7l1 13h8l1-13" />
  </svg>
);

/** Success tone — completed, sent, saved. */
export const ModalCheckIcon = ({ size = 19 }: GlyphProps) => (
  <svg {...base(size)}>
    <path d="M4 12.5l5 5L20 6.5" />
  </svg>
);

/** Neutral tone — informational, confirmations, warnings without data loss. */
export const ModalAlertIcon = ({ size = 19 }: GlyphProps) => (
  <svg {...base(size)}>
    <path d="M12 3l9 17H3z" />
    <path d="M12 10v4M12 17h.01" />
  </svg>
);

/** Amber tone — plan-limit, "upgrade required". */
export const ModalLockIcon = ({ size = 19 }: GlyphProps) => (
  <svg {...base(size)}>
    <rect x="4" y="10" width="16" height="11" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

/** Header close affordance. Heavier stroke than the tile glyphs, per the mock. */
export const ModalCloseIcon = ({ size = 16 }: GlyphProps) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    width={size}
    height={size}
    aria-hidden
    focusable="false"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);
